import React, { FC, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';

interface PopupTransitionProps {
  open: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  children?: React.ReactNode;
  closeOnEscape?: boolean;
  closeOnDocumentClick?: boolean;
}

const PopupTransition: FC<PopupTransitionProps> = ({
  open,
  setOpen,
  onClose,
  children,
  closeOnEscape = true,
  closeOnDocumentClick = true,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const historyStateRef = useRef<string | null>(null);

  const handleClose = () => {
    if (closeOnDocumentClick) {
      if (historyStateRef.current) {
        window.history.back();
        historyStateRef.current = null;
      }

      setOpen && setOpen(false);
      onClose && onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        handleClose();
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (closeOnDocumentClick && backdropRef.current && event.target === backdropRef.current) {
        handleClose();
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (open && historyStateRef.current) {
        historyStateRef.current = null;
        setOpen && setOpen(false);
        onClose && onClose();
      }
    };

    const handleScroll = (event: Event) => {
      if (event.target === backdropRef.current) {
        event.preventDefault();
        return false;
      }
    };

    if (open) {
      // Add event listeners
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleOutsideClick);
      window.addEventListener('popstate', handlePopState);
      backdropRef.current?.addEventListener('wheel', handleScroll, {
        passive: false,
      });
      backdropRef.current?.addEventListener('touchmove', handleScroll, {
        passive: false,
      });

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      if (!historyStateRef.current) {
        historyStateRef.current = `popup-${Date.now()}`;
        window.history.pushState({ popup: historyStateRef.current }, '', window.location.href);
      }
    }

    return () => {
      // Clean up event listeners
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('popstate', handlePopState);
      backdropRef.current?.removeEventListener('wheel', handleScroll);
      backdropRef.current?.removeEventListener('touchmove', handleScroll);

      // Restore body scroll
      document.body.style.overflow = '';
    };
  }, [open, closeOnEscape, closeOnDocumentClick]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden">
          <motion.div
            ref={backdropRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 overflow-hidden bg-black/50"
          />
          <motion.div
            ref={popupRef}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => {
              if (e.target === popupRef.current) {
                handleClose();
              }
            }}
            className="relative z-[10000] flex h-full min-h-full w-full items-center justify-center"
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default PopupTransition;
